import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { getIsoStringToDate } from "../../../utils/utils";

const CIRCLE_SIZE = 32;

const getStatusColor = (status) => {
  if (status === "APPROVED") return "#4caf50"; // green
  if (status === "REJECTED") return "#f44336"; // red
  if (status === "PENDING") return "#1976d2"; // blue
  return "#bdbdbd"; // gray
};

const Stepper = ({
  stepsData,
  steps,
  setSteps,
  onAction,
  hideProgressSeries,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  // Initialize steps if not provided by parent
  useEffect(() => {
    if (stepsData.length > 0 && (!steps || steps.length === 0)) {
      setSteps(stepsData);
    }
  }, [stepsData, steps, setSteps]);

  const handleCheckboxClick = (index) => {
    if (steps[index].status === "PENDING") {
      setActiveIndex(index);
      setComment(steps[index].comment || "");
      setError("");
    }
  };

  const handleClose = () => {
    setActiveIndex(null);
    setComment("");
    setError("");
  };

  const handleLocalAction = (type) => {
    if (!comment.trim()) {
      setError("Comment is mandatory");
      return;
    }
    if (comment.length > 200) {
      setError("Comment cannot be more than 200 characters");
      return;
    }

    // Call parent's onAction with current index, type, and comment
    onAction(activeIndex, type, comment);

    handleClose();
  };

  const getConnectorColor = (index) => {
    const nextStepStatus = steps[index + 1]?.status;

    if (nextStepStatus === "REJECTED") {
      return "#f44336";
    }
    if (steps[index].status === "APPROVED" && nextStepStatus === "APPROVED") {
      return "#4caf50";
    }
    if (steps[index].status === "APPROVED" && nextStepStatus !== "APPROVED") {
      return "#4caf50";
    }
    return "#bdbdbd";
  };

  if (!steps || steps.length === 0) return null; // or loading state

  return (
    <Box
      sx={{
        margin: "20px 20px",
        position: "relative",
        userSelect: "none",
        paddingLeft: CIRCLE_SIZE / 2,
        paddingRight: CIRCLE_SIZE / 2,
      }}
    >
      {/* Connector Bar */}
      <Box
        sx={{
          position: "absolute",
          top: CIRCLE_SIZE / 2,
          left: CIRCLE_SIZE / 2,
          right: CIRCLE_SIZE / 2,
          height: 4,
          display: "flex",
          zIndex: 0,
        }}
      >
        {steps.map((step, index) =>
          index !== steps.length - 1 ? (
            <Box
              key={index}
              sx={{
                flex: 1,
                height: 4,
                bgcolor: getConnectorColor(index),
                transition: "background-color 0.3s ease",
              }}
            />
          ) : null,
        )}
      </Box>

      {/* Steps */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
          minWidth: "max-content",
        }}
      >
        {steps?.map((step, index) => (
          <Box
            key={step.approvalStepLevel}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: CIRCLE_SIZE * 2,
              marginLeft: index === 0 ? -CIRCLE_SIZE / 2 : 0,
              marginRight: index === steps.length - 1 ? -CIRCLE_SIZE / 2 : 0,
            }}
          >
            {/* Circle */}
            {!hideProgressSeries && (
              <Box
                sx={{
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  borderRadius: "50%",
                  bgcolor: getStatusColor(step.status),
                  color: "#fff",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  userSelect: "none",
                  cursor: step.status === "PENDING" ? "pointer" : "default",
                }}
                onClick={() => handleCheckboxClick(index)}
              >
                {index + 1}
              </Box>
            )}

            {/* Checkbox */}
            <Checkbox
              checked={step.status === "APPROVED"}
              disabled={step.status !== "PENDING"}
              onChange={() => handleCheckboxClick(index)}
              sx={{ padding: 0, marginTop: 0.5 }}
            />

            {/* Label */}
            <Typography
              variant="caption"
              sx={{ textAlign: "center", userSelect: "none" }}
            >
              {step.approvalStepName}
            </Typography>
            <Tooltip
              title={
                <div style={{ maxWidth: 320, fontSize: 13, lineHeight: 1.4 }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: 4,
                      textDecoration: "underline",
                    }}
                  >
                    Step Description
                  </div>
                  <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                    {step.approvalDescription}
                  </div>

                  {/* Assigned Reviewers Section */}
                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: 4,
                      textDecoration: "underline",
                    }}
                  >
                    Assigned Reviewers
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    {(step.approvalStepApprovers || []).map((approver) => (
                      <div
                        key={approver.email}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginBottom: 2,
                          fontStyle: approver.approverIsActive
                            ? "normal"
                            : "italic",
                          color: approver.approverIsActive ? "#fcfcfc" : "#999",
                        }}
                        title={approver.email}
                      >
                        {approver.email}
                        {!approver.approverIsActive && " (Inactive)"}
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: 6,
                      textDecoration: "underline",
                    }}
                  >
                    Reviewers History:
                  </div>

                  {(() => {
                    // Collect all approvalData from all approvers
                    const allApprovalData = (
                      step.approvalStepApprovers || []
                    ).flatMap((approver) =>
                      (approver.approvalDataList || []).map((ad) => ({
                        ...ad,
                        approverEmail: approver.email,
                        approverIsActive: approver.approverIsActive,
                      })),
                    );

                    if (allApprovalData.length === 0) {
                      return (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ fontWeight: "600", color: "#ff3e3e" }}>
                            No Approval Data found!
                          </span>
                        </div>
                      );
                    }

                    // Sort descending by approvalStatusChangeTime
                    allApprovalData.sort((a, b) => {
                      const dateA = new Date(a.approvalStatusChangeTime);
                      const dateB = new Date(b.approvalStatusChangeTime);
                      return dateB - dateA;
                    });

                    return allApprovalData.map((ad, idx) => {
                      const status = ad.approvalStatus;
                      let statusColor = "#999"; // default gray
                      if (status === "APPROVED")
                        statusColor = "#4caf50"; // green
                      else if (status === "REJECTED")
                        statusColor = "#f44336"; // red
                      else statusColor = "#1976d2"; // blue

                      return (
                        <div
                          key={`${ad.approvalStepApproverStepApprovalId}-${idx}`}
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: 6,
                            padding: "8px 12px",
                            marginBottom: 10,
                            backgroundColor: "#f9f9f9",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 4,
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                color: "#555",
                                display: "inline-block",
                                minWidth: 70,
                                userSelect: "none",
                              }}
                            >
                              Approver
                            </span>
                            <span
                              style={{
                                minWidth: 4,
                                textAlign: "center",
                                userSelect: "none",
                                color: "#555",
                              }}
                            >
                              :
                            </span>
                            <span
                              style={{
                                fontStyle: "italic",
                                color: "#333",
                                wordBreak: "break-word",
                                flexGrow: 1,
                              }}
                              title={ad.approverEmail}
                            >
                              {ad.approverEmail}
                              {!ad.approverIsActive && " (Inactive)"}
                            </span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: ad.approvalComments ? 4 : 0,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                color: "#555",
                                display: "inline-block",
                                minWidth: 70,
                                userSelect: "none",
                              }}
                            >
                              Status
                            </span>
                            <span
                              style={{
                                minWidth: 4,
                                textAlign: "center",
                                userSelect: "none",
                                color: "#555",
                              }}
                            >
                              :
                            </span>
                            <span
                              style={{
                                fontWeight: "600",
                                color: statusColor,
                                textTransform: "uppercase",
                              }}
                            >
                              {status}
                            </span>
                            <span
                              style={{
                                fontWeight: "400",
                                fontSize: 12,
                                color: "#333",
                                marginLeft: 90,
                              }}
                            >
                              ({getIsoStringToDate(ad.approvalStatusChangeTime)}
                              )
                            </span>
                          </div>

                          {ad.approvalComments && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 4,
                                flexWrap: "wrap",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "600",
                                  color: "#555",
                                  display: "inline-block",
                                  minWidth: 70,
                                  userSelect: "none",
                                }}
                              >
                                Comments
                              </span>
                              <span
                                style={{
                                  minWidth: 4,
                                  textAlign: "center",
                                  userSelect: "none",
                                  color: "#555",
                                }}
                              >
                                :
                              </span>
                              <span
                                style={{
                                  fontStyle: "italic",
                                  fontSize: 12,
                                  color: "#333",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  flexGrow: 1,
                                  display: "inline-block",
                                }}
                              >
                                {ad.approvalComments}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              }
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  cursor: "pointer",
                  color: "#4992f7",
                }}
              />
            </Tooltip>
          </Box>
        ))}
      </Box>

      {/* Dialog */}
      <Dialog open={activeIndex !== null} onClose={handleClose} fullWidth>
        <DialogTitle>
          Add Comment
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={comment}
            placeholder="Enter Comments within 200 Characters..."
            onChange={(e) => {
              setComment(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleLocalAction("REJECTED")}
          >
            Reject
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={() => handleLocalAction("APPROVED")}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stepper;
